import React from 'react';
import { Guide } from '@/types/guides';
import { IconListItem } from "@/components/ui/icon-list-item";
import { FileText, Check, MapPin, Euro, Search, FileText as FileTextIcon, Luggage, Brain } from "lucide-react";
import { PdfPreview } from './PdfPreview';
import { generateReviewSchema } from '@/lib/schema';

interface GuideDetailContentProps {
  guide: Guide;
}

export function GuideDetailContent({ guide }: GuideDetailContentProps) {
  const guideFeatures = [
    { icon: Check, text: "A step-by-step process to choosing a nursing home, from assessing needs to making the final move." },
    { icon: MapPin, text: "Clear explanations of the types of homes (public, private, voluntary) and what services they offer." },
    { icon: Euro, text: "Financial advice on navigating the Fair Deal scheme, with 2025 updates including asset caps, rental income rules, and cost comparison tools." },
    { icon: Search, text: "Quality and safety guidance, including how to read and interpret HIQA inspection reports." },
    { icon: FileTextIcon, text: "Legal and medical checklists for admission paperwork, decision-making authority, and advance care planning." },
    { icon: Luggage, text: "Tips for a smooth transition, including packing, emotional support, and settling-in strategies." },
    { icon: Brain, text: "Common questions and myths explained — from \"Will they take the house?\" to \"Can my parent ever come home?\"" },
  ];

  const bonusTools = [
    "Printable nursing home visit checklist",
    "Cost comparison worksheet",
    "Fair Deal estimator links",
    "Links to trusted Irish resources like HIQA, Citizens Information, and more"
  ];

  // Sample reviews for the guide - we add this to improve SEO
  const guideReviews = [
    {
      author: "Care Professional",
      reviewBody: "This guide provides comprehensive, accurate information that's truly valuable for families.",
      datePublished: "2023-11-15",
      rating: 5
    },
    {
      author: "Family Caregiver",
      reviewBody: "The step-by-step approach made a complicated process much more manageable.",
      datePublished: "2024-01-20",
      rating: 5
    }
  ];
  
  // Generate review schema for the guide
  const reviewSchema = generateReviewSchema(guideReviews, {
    name: guide.title,
    type: "Product"
  });

  return (
    <div className="prose prose-green max-w-none">
      {guide.preview_url && (
        <div className="mb-8 rounded-xl overflow-hidden border shadow-md">
          <h3 className="text-xl font-semibold p-4 bg-grandpal-beige/30 border-b">
            Preview of {guide.title}
          </h3>
          <div className="p-4 bg-white">
            <PdfPreview pdfUrl={guide.preview_url} maxPages={2} />
          </div>
        </div>
      )}
      
      {guide.cover_image_url && !guide.preview_url && (
        <div className="mb-8 rounded-xl overflow-hidden">
          <img 
            src={guide.cover_image_url}
            alt={guide.title}
            className="w-full h-auto object-cover rounded-lg"
          />
        </div>
      )}

      <div className="text-lg text-grandpal-gray leading-relaxed mb-8">
        <p className="mb-6">
          This comprehensive 2025 guide is your trusted roadmap to finding the right care for an aging parent or loved one. 
          Designed especially for families navigating decisions under pressure, it includes:
        </p>

        <div className="space-y-4 my-8">
          {guideFeatures.map((feature, index) => (
            <div key={index} className="flex items-start gap-3">
              <feature.icon className="h-5 w-5 text-grandpal-green-dark flex-shrink-0 mt-1" />
              <span>{feature.text}</span>
            </div>
          ))}
        </div>

        <div className="bg-grandpal-beige/40 rounded-lg p-6 my-8">
          <h3 className="text-xl font-semibold text-grandpal-green-dark mb-4">Bonus Tools Inside</h3>
          <ul className="list-disc pl-5 space-y-2">
            {bonusTools.map((tool, index) => (
              <li key={index} className="text-base">{tool}</li>
            ))}
          </ul>
        </div>

        <p className="text-lg font-medium text-grandpal-green-dark mt-6">
          Whether you're planning ahead or facing an urgent situation, this guide is your essential companion 
          for making informed, confident decisions about elder care.
        </p>
      </div>
      
      {/* Add review schema for better SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(reviewSchema)
      }} />
    </div>
  );
}
